import { lazy, type ComponentType } from "react";

/**
 * lazyWithRetry — guards route code-splitting against stale build chunks.
 *
 * When a new version is deployed, the hashed chunk filenames change. A tab that
 * was opened against the previous build still asks for the old filename, which
 * no longer exists, and the dynamic import rejects with
 * "Failed to fetch dynamically imported module" — a blank screen.
 *
 * Strategy: retry once (covers a transient network blip), then force a single
 * hard reload so the browser picks up the current index.html and its new chunk
 * names. A sessionStorage flag makes sure we never reload in a loop.
 */
const RELOAD_FLAG = "os:chunk-reloaded";

export function lazyWithRetry<T extends ComponentType<unknown>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      window.sessionStorage.removeItem(RELOAD_FLAG);
      return mod;
    } catch (err) {
      // One quiet retry for transient failures.
      try {
        const mod = await factory();
        window.sessionStorage.removeItem(RELOAD_FLAG);
        return mod;
      } catch (err2) {
        const alreadyReloaded =
          window.sessionStorage.getItem(RELOAD_FLAG) === "1";
        if (!alreadyReloaded) {
          window.sessionStorage.setItem(RELOAD_FLAG, "1");
          window.location.reload();
          // Never resolves — the reload replaces this document.
          return new Promise<{ default: T }>(() => {});
        }
        throw err2;
      }
    }
  });
}
